import { Box } from "@mui/material";
import { useState } from "react";

import AiSectionFeedback from "./../../Components/AiSectionFeedback";

import HeaderSection from "./HeaderSection";
import BusinessFocusCard from "./BusinessFocusCard";
import FinancialsCard from "./FinancialsCard";
import RecentNewsCard from "./RecentNewsCard";
import RiskOverviewSection from "./RiskOverviewSection";
import RiskModal from "./RiskModal";
import InvestmentCommentarySection from "./InvestmentCommentarySection";
import OpportunitiesSection from "./OpportunitiesSection";
import OpportunityModal from "./OpportunityModal";

const EntityProfile = ({ data }) => {
  const [openRisk, setOpenRisk] = useState(null);
  const [openOpportunity, setOpenOpportunity] = useState(null);

  const core = data?.client_360_overview?.core;
  const businessFocus = data?.client_360_overview?.business_focus;
  const financials = data?.client_360_overview?.financials;
  const news = data?.recent_news?.news || [];
  const riskOverview = data?.risk_overview_view;
  const risks = riskOverview?.risks || [];
  const overallScore = riskOverview?.overall_score;
  const investmentCommentary = data?.investment_commentary || [];
  const opportunities = data?.opportunities || {};
  const crossSell = opportunities?.cross_sell_opportunities || [];
  const retentionOpps = opportunities?.retention_opportunities || [];

  const mappedRisks = risks.map((r) => {
    const score = Number(r.score_out_of_5);

    let dotColor = "#FFB74D"; // default (medium)
    let level = "Medium";

    if (!Number.isNaN(score)) {
      if (score <= 2) {
        level = "Low";
        dotColor = "#2E7D32"; // dark green
      } else if (score < 4) {
        level = "Medium";
        dotColor = "#F9A825"; // dark amber
      } else {
        level = "High";
        dotColor = "#C62828"; // dark red
      }
    }

    return {
      id: r.name,
      title: r.name,
      scoreLabel: `Score: ${score || "–"} / 5`,
      level,
      dotColor,
      summary: r.description || "",
      value: r.value || "",
      rationale: r.rationale || "",
      description: r.description || "",
    };
  });

  const aiOpportunities = [
    ...retentionOpps.map((o) => ({
      id: `ret-${o.strategy}`,
      type: "retention",
      title: o.strategy,
      issue: o.issue,
      recommended_action: o.recommended_action,
      linked_risk: o.linked_risk,
      rationale: `Issue: ${o.issue}\n—\n${o.recommended_action}`,
    })),

    ...crossSell.map((o) => ({
      id: `cs-${o.target_product_group}`,
      type: "cross_sell",
      title: o.target_product_group,
      rationale: o.rationale,
      examples: o.examples || [],
    })),
  ];

  const handleOpenRisk = (risk) => setOpenRisk(risk);
  const handleCloseRisk = () => setOpenRisk(null);

  const handleOpenOpportunity = (opp) => setOpenOpportunity(opp);
  const handleCloseOpportunity = () => setOpenOpportunity(null);

  const clientName = core?.client_name || data?.client_id || "Client";
  const accountLead = core?.pfg_account_lead || "-";
  const sponsors = core?.sponsors || [];

  const primaryRegions = businessFocus?.primary_regions || [];
  const focusAreas = businessFocus?.investment_focus_areas || [];
  const consultants = businessFocus?.consultants || [];

  const clientFinancials = financials?.client_financials || [];

  const overallScoreValue =
    typeof overallScore?.score_out_of_5 === "number"
      ? overallScore.score_out_of_5.toFixed(1)
      : "–";

  const entityId = data?.client_id || core?.client_name || "";
  const requestId =
    data?.request_id ||
    data?.requestId ||
    data?.api_request_id ||
    data?.apiRequestId ||
    "12345";

  const userEmail = process.env.REACT_APP_USER;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f8f9fa",
        color: "#212529",
        px: { xs: 2, md: 3 },
        py: 3,
      }}
    >
      <Box
        sx={{
          width: "96vw",
          mx: "auto",
          px: { xs: 1, sm: 2, md: 3 },
          py: 15,
          pt: 5,
        }}
      >
        {/* HEADER */}
        <HeaderSection
          clientName={clientName}
          accountLead={accountLead}
          sponsors={sponsors}
          segmentation={businessFocus?.segmentation || "Client Segment"}
        />

        {/* TOP 3 CARDS */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: 3,
            alignItems: "stretch",
          }}
        >
          <BusinessFocusCard
            primaryRegions={primaryRegions}
            focusAreas={focusAreas}
            consultants={consultants}
          />

          <FinancialsCard
            financials={financials}
            clientFinancials={clientFinancials}
          />

          <RecentNewsCard
            news={news}
            entityId={entityId}
            AiSectionFeedback={AiSectionFeedback}
            requestId={requestId}
            uiSectionKey="recent_news"
            userEmail={userEmail}
          />
        </Box>

        {/* INVESTMENT COMMENTARY (AI) */}
        <InvestmentCommentarySection
          investmentCommentary={investmentCommentary}
          entityId={entityId}
          AiSectionFeedback={AiSectionFeedback}
          requestId={requestId}
          uiSectionKey="investment_commentary"
          userEmail={userEmail}
        />

        {/* RISK OVERVIEW (AI-reviewed) */}
        <RiskOverviewSection
          overallScore={overallScore}
          overallScoreValue={overallScoreValue}
          mappedRisks={mappedRisks}
          onOpenRisk={handleOpenRisk}
          entityId={entityId}
          AiSectionFeedback={AiSectionFeedback}
          requestId={requestId}
          uiSectionKey="churn_risk_overview"
          userEmail={userEmail}
        />

        {/* RISK MODAL */}
        <RiskModal openRisk={openRisk} onClose={handleCloseRisk} />

        {/* AI OPPORTUNITIES */}
        <OpportunitiesSection
          aiOpportunities={aiOpportunities}
          onOpenOpportunity={handleOpenOpportunity}
          entityId={entityId}
          AiSectionFeedback={AiSectionFeedback}
          requestId={requestId}
          uiSectionKey="ai_recommended_opportunities"
          userEmail={userEmail}
        />

        {/* OPPORTUNITY MODAL */}
        <OpportunityModal
          openOpportunity={openOpportunity}
          onClose={handleCloseOpportunity}
        />
      </Box>
    </Box>
  );
};

export default EntityProfile;
